/**
 * Scope-Aware Predecessors
 *
 * Computes the set of action names whose outputs are visible to a target
 * action regardless of where it lives in the workflow tree (top-level or
 * nested inside Foreach / If / Switch / Until / Scope / Agent tools).
 *
 * Logic Apps `runAfter` references are scoped — an action can only depend
 * on its siblings within the same `actions` block. So the visible token
 * set for an action `A` is:
 *
 *   visible(A) =
 *     transitive runAfter siblings of A in A's own scope
 *     ∪ visible(parentScopeAction) for each enclosing scope action
 *
 * The parent scope actions themselves are intentionally NOT added — their
 * "outputs" (e.g., a Foreach) aren't useful as tokens; iteration values
 * are exposed separately via `items()` / `iterationIndexes()`.
 *
 * This module is the single source of truth used by:
 * - `useTokenSources` — token picker dynamic-content sources
 * - `useUpstreamManifests` — upstream manifest/swagger fetcher
 * - `WorkflowInputPanel` — inline rail UI
 */

import type { Action } from '@/types/workflowDefinition';

/**
 * Where an action lives within the workflow tree.
 */
export interface ActionLocation {
  /** Action name. */
  name: string;
  /** The `actions` record this action belongs to (its parent scope). */
  siblings: Record<string, Action>;
  /** Names of enclosing scope actions, innermost first. Empty when top-level. */
  scopePath: string[];
}

interface ChildScope {
  actions: Record<string, Action>;
  scopePath: string[];
}

/**
 * Yield the child `actions` records nested directly inside an action.
 * Handles all known scope shapes: Foreach/Until/Scope, If true/else,
 * Switch cases/default, and Agent tools.
 */
function getChildScopes(
  action: Action,
  parentName: string,
  parentScopePath: string[]
): ChildScope[] {
  const raw = action as Record<string, unknown>;
  const childPath = [parentName, ...parentScopePath];
  const result: ChildScope[] = [];

  // Direct .actions (Foreach / Until / Scope / If true-branch)
  const direct = raw['actions'];
  if (direct && typeof direct === 'object' && !Array.isArray(direct)) {
    result.push({ actions: direct as Record<string, Action>, scopePath: childPath });
  }

  // .else.actions
  const elseBlock = raw['else'] as Record<string, unknown> | undefined;
  if (elseBlock && typeof elseBlock['actions'] === 'object' && elseBlock['actions'] !== null) {
    result.push({
      actions: elseBlock['actions'] as Record<string, Action>,
      scopePath: childPath,
    });
  }

  // .cases[*].actions
  const cases = raw['cases'] as Record<string, Record<string, unknown>> | undefined;
  if (cases && typeof cases === 'object') {
    for (const caseEntry of Object.values(cases)) {
      if (caseEntry && typeof caseEntry['actions'] === 'object' && caseEntry['actions'] !== null) {
        result.push({
          actions: caseEntry['actions'] as Record<string, Action>,
          scopePath: childPath,
        });
      }
    }
  }

  // .default.actions
  const defaultBlock = raw['default'] as Record<string, unknown> | undefined;
  if (defaultBlock && typeof defaultBlock['actions'] === 'object' && defaultBlock['actions'] !== null) {
    result.push({
      actions: defaultBlock['actions'] as Record<string, Action>,
      scopePath: childPath,
    });
  }

  // Agent .tools[*].actions
  const tools = raw['tools'] as Record<string, Record<string, unknown>> | undefined;
  if (tools && typeof tools === 'object') {
    for (const tool of Object.values(tools)) {
      if (tool && typeof tool['actions'] === 'object' && tool['actions'] !== null) {
        result.push({
          actions: tool['actions'] as Record<string, Action>,
          scopePath: childPath,
        });
      }
    }
  }

  return result;
}

/**
 * Locate an action by name anywhere in the workflow tree.
 * Returns undefined if not found.
 *
 * Convenience wrapper around `buildLocationIndex` for one-off lookups.
 * When you need many lookups against the same tree (e.g. inside the
 * predecessor walk), build the index once and consult the map directly
 * to avoid O(N) tree walks per query.
 */
export function findActionLocation(
  rootActions: Record<string, Action>,
  target: string
): ActionLocation | undefined {
  return buildLocationIndex(rootActions).get(target);
}

/**
 * Build a `name → ActionLocation` map in a single tree pass.
 *
 * Action names are workflow-globally unique, so a flat map is safe.
 * If a duplicate is encountered, the first occurrence wins (consistent
 * with depth-first definition order).
 */
function buildLocationIndex(
  rootActions: Record<string, Action>
): Map<string, ActionLocation> {
  const index = new Map<string, ActionLocation>();
  collectIndex(rootActions, [], index);
  return index;
}

function collectIndex(
  actions: Record<string, Action>,
  scopePath: string[],
  out: Map<string, ActionLocation>
): void {
  for (const [name, action] of Object.entries(actions)) {
    if (!out.has(name)) {
      out.set(name, { name, siblings: actions, scopePath });
    }
    for (const child of getChildScopes(action, name, scopePath)) {
      collectIndex(child.actions, child.scopePath, out);
    }
  }
}

/**
 * Recursively flatten all actions in the workflow tree into a single
 * `name → action` record. Sibling scope blocks share the workflow-wide
 * unique-name invariant, so collisions don't occur in valid workflows.
 *
 * Order: definition order, depth-first — parents appear before their
 * descendants. Used by callers that want to enumerate every possible
 * token source action.
 */
export function flattenAllActions(
  rootActions: Record<string, Action>
): Record<string, Action> {
  const result: Record<string, Action> = {};
  collectFlat(rootActions, result);
  return result;
}

function collectFlat(
  actions: Record<string, Action>,
  out: Record<string, Action>
): void {
  for (const [name, action] of Object.entries(actions)) {
    out[name] = action;
    for (const child of getChildScopes(action, name, [])) {
      collectFlat(child.actions, out);
    }
  }
}

/**
 * Resolve a `runAfter` key to a real action somewhere in the tree.
 *
 * Logic Apps `runAfter` keys may be:
 *   - a plain action name (sibling)
 *   - a path-style reference like `"Condition/HTTP_3"` for an action
 *     nested inside a scope (Otto generates these for top-level actions
 *     that depend on the terminal of an If/Switch branch)
 *   - a plain leaf name that belongs to a nested action (Otto also emits
 *     `runAfter: { HTTP_4: [...] }` on a top-level action even though
 *     `HTTP_4` lives inside a Condition's else branch)
 *
 * Action names are workflow-globally unique, so the leaf name uniquely
 * identifies the target.
 */
function resolveRunAfterKey(
  rawKey: string,
  index: Map<string, ActionLocation>
): ActionLocation | undefined {
  const leaf = rawKey.includes('/') ? rawKey.split('/').pop()! : rawKey;
  return index.get(leaf);
}

/**
 * BFS the predecessor graph starting from `startName`.
 *
 * The graph has two kinds of edges:
 *   - **Explicit**: `runAfter` keys, resolved across the whole tree
 *     (handles cross-scope references like `Condition/HTTP_3` or a
 *     bare `HTTP_4` referencing an action nested in a branch).
 *   - **Implicit**: enclosing-scope inheritance — an action inside a
 *     Foreach/If/Switch logically runs after every predecessor of the
 *     scope action itself.
 *
 * Visited targets reached via an explicit edge are added to `result`.
 * Targets reached only via implicit scope-inheritance are NOT added —
 * the scope action (Foreach/If/etc.) doesn't have useful outputs of
 * its own; its predecessors do, and they enter `result` when their
 * `runAfter` chains are walked.
 *
 * Uses a prebuilt `name → ActionLocation` index and an index-based
 * queue cursor (no `.shift()`) so the whole walk is O(N + E) instead
 * of O(N^2).
 */
function walkPredecessors(
  startName: string,
  index: Map<string, ActionLocation>,
  result: Set<string>
): void {
  interface Frame {
    name: string;
    /** True when reached via an explicit runAfter edge. */
    addToResult: boolean;
  }

  const visited = new Set<string>();
  const queue: Frame[] = [{ name: startName, addToResult: false }];
  let head = 0;

  while (head < queue.length) {
    const frame = queue[head++]!;
    if (visited.has(frame.name)) continue;
    visited.add(frame.name);

    if (frame.addToResult) result.add(frame.name);

    const loc = index.get(frame.name);
    if (!loc) continue;
    const action = loc.siblings[frame.name] as
      | { runAfter?: Record<string, string[]> }
      | undefined;

    // Explicit runAfter edges (resolved across the tree so cross-scope
    // references like `Condition/HTTP_3` or bare `HTTP_4` work).
    if (action?.runAfter) {
      for (const rawKey of Object.keys(action.runAfter)) {
        const predLoc = resolveRunAfterKey(rawKey, index);
        if (!predLoc || visited.has(predLoc.name)) continue;
        queue.push({ name: predLoc.name, addToResult: true });
      }
    }

    // Implicit scope inheritance: each enclosing scope's predecessors
    // are also reachable from inside.
    for (const ancestorName of loc.scopePath) {
      if (visited.has(ancestorName)) continue;
      queue.push({ name: ancestorName, addToResult: false });
    }
  }
}

/**
 * Compute the set of action names whose outputs are visible to the
 * action `currentActionName`.
 *
 * Includes:
 * - Transitive `runAfter` predecessors, with cross-scope references
 *   (path-style `Parent/Child` or bare leaf names) resolved against the
 *   whole workflow tree.
 * - Predecessors inherited from each enclosing scope (so an action
 *   inside a ForEach can see actions that ran before the ForEach).
 *
 * Excludes:
 * - The action itself.
 * - Enclosing scope actions reached only via scope-inheritance — their
 *   outputs aren't useful tokens (e.g., a ForEach has no outputs; use
 *   `items()` instead). Scope actions DO appear when reached via an
 *   explicit `runAfter` edge from a sibling, since that's a normal
 *   downstream dependency.
 * - Trigger names — callers handle triggers separately.
 *
 * Fallback: when `currentActionName` is undefined, returns ALL actions
 * (flattened) so the picker has content to display when nothing is
 * selected. When a name IS provided but the action can't be located,
 * returns an empty set — "all actions" would silently break scoping by
 * surfacing every output as a candidate predecessor.
 */
export function getScopeAwarePredecessors(
  currentActionName: string | undefined,
  rootActions: Record<string, Action>
): Set<string> {
  const index = buildLocationIndex(rootActions);

  if (!currentActionName) {
    return new Set(index.keys());
  }

  if (!index.has(currentActionName)) {
    return new Set();
  }

  const result = new Set<string>();
  walkPredecessors(currentActionName, index, result);
  return result;
}
